// src/components/gestionEvento/MobilePruebasView.jsx
import React, { useMemo, useState } from 'react';
import {
  Box, Typography, Stack, IconButton, Divider, List, ListItemButton,
  ListItemSecondaryAction, alpha, InputBase, Button, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions, Chip, Checkbox
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteIcon from '@mui/icons-material/Delete';

import CustomPruebaModal from '../CustomPruebaModal';
import PredefinedPruebasModal from '../PredefinedPruebasModal';

const EMPTY_PRUEBA = {
  id: '',
  nombre: '',
  testType: '',
  eventCode: '',
  tipoMarca: '',
  descripcion: '',
  categoriaIdsFemenil: [],
  categoriaIdsVaronil: [],
};

function PruebaItem({
  p,
  onOpenEdit,
  checked,
  onToggleCheck,
}) {
  // ⬇️ Solo mostramos el TIPO en el subtítulo
  const subtitle = p?.testType || '';

  return (
    <Box sx={{ mb: 1.25, width: '100%' }}>
      <ListItemButton
        onClick={() => onOpenEdit?.(p)}
        sx={(t) => ({
          width: '100%',
          alignItems: 'flex-start',
          borderRadius: 3,
          bgcolor: alpha(t.palette.common.black, 0.015),
          border: `1px solid ${alpha(t.palette.text.primary, 0.08)}`,
          '&:hover': { bgcolor: alpha(t.palette.primary.main, 0.04) },
          ...(checked
            ? {
                bgcolor: alpha(t.palette.error.main, 0.08),
                border: `2px solid ${alpha(t.palette.error.main, 0.6)}`,
              }
            : {}),
        })}
      >
        {/* checkbox de selección */}
        <Checkbox
          edge="start"
          checked={!!checked}
          onClick={(e) => e.stopPropagation()}
          onChange={() => onToggleCheck?.(p.id)}
          sx={{ mr: 1, mt: 0.25 }}
        />

        {/* Contenido */}
        <Stack spacing={0.5} sx={{ width: '100%', pr: 6 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: 1 }}>
            <Typography
              sx={{
                fontSize: '1.02rem',
                fontWeight: 700,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {p?.nombre || 'Sin nombre'}
            </Typography>
            {/* ⬇️ Conservamos el Chip con el eventCode */}
            {!!p?.eventCode && (
              <Chip size="small" label={p.eventCode} sx={{ fontWeight: 700 }} />
            )}
          </Stack>

          {/* Subtítulo (solo tipo) */}
          {subtitle && (
            <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
              {subtitle}
            </Typography>
          )}

          {/* Descripción en una línea (opcional) */}
          {p?.descripcion ? (
            <Typography
              sx={{
                color: 'text.disabled',
                fontSize: '0.85rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {p.descripcion}
            </Typography>
          ) : null}
        </Stack>

        <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton edge="end" aria-label="abrir" onClick={() => onOpenEdit?.(p)}>
            <ChevronRightIcon />
          </IconButton>
        </ListItemSecondaryAction>
      </ListItemButton>
      <Divider sx={{ opacity: 0.2 }} />
    </Box>
  );
}

export default function MobilePruebasView({
  pruebas = [],
  categorias = [],
  search = '',
  setSearch = () => {},
  onUpdatePrueba,
  onDeletePrueba,
  onAddPruebaCustom,
  onAddSelectedPruebas,
  onRequestDeleteMultiple,       // opcional
  setSnackbar,
}) {
  // filtro
  const filtered = useMemo(() => {
    const q = (search || '').toLowerCase().trim();
    if (!q) return pruebas;
    return pruebas.filter((p) => {
      const inNombre = p?.nombre?.toLowerCase().includes(q);
      const inEvent  = p?.eventCode?.toLowerCase?.().includes(q);
      const inTipo   = p?.testType?.toLowerCase?.().includes(q);
      return inNombre || inEvent || inTipo;
    });
  }, [pruebas, search]);

  // selección múltiple
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const toggleCheck = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  // modales
  const [openCustomModal, setOpenCustomModal] = useState(false);
  const [customMode, setCustomMode] = useState('edit'); // 'edit' | 'create'
  const [editData, setEditData] = useState(EMPTY_PRUEBA);
  const [openPredefModal, setOpenPredefModal] = useState(false);

  // confirmación eliminación individual (por compatibilidad)
  const [toDelete, setToDelete] = useState(null);

  const handleOpenEdit = (prueba) => {
    setCustomMode('edit');
    setEditData({ ...EMPTY_PRUEBA, ...prueba });
    setOpenCustomModal(true);
  };

  const handleOpenCreate = () => {
    setCustomMode('create');
    setEditData({ ...EMPTY_PRUEBA });
    setOpenCustomModal(true);
  };

  const handleSaveCustom = async () => {
    if (!editData?.nombre?.trim() || !editData?.testType) {
      setSnackbar?.({ open: true, message: 'Nombre y Tipo de Prueba son obligatorios.', severity: 'warning' });
      return;
    }
    try {
      if (customMode === 'edit') {
        await onUpdatePrueba?.(editData.id, editData);
        setSnackbar?.({ open: true, message: 'Prueba actualizada.', severity: 'success' });
      } else {
        if (!editData?.eventCode || !editData?.tipoMarca) {
          setSnackbar?.({ open: true, message: 'EventCode y Tipo Marca son obligatorios para crear.', severity: 'warning' });
          return;
        }
        await onAddPruebaCustom?.(editData);
      }
      setOpenCustomModal(false);
    } catch (e) {
      setSnackbar?.({ open: true, message: e?.message || 'Error al guardar.', severity: 'error' });
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      if (onRequestDeleteMultiple) {
        await onRequestDeleteMultiple(ids);
      } else if (onDeletePrueba) {
        await Promise.all(ids.map((id) => onDeletePrueba(id)));
      }
      setSnackbar?.({
        open: true,
        message: `${ids.length} prueba${ids.length > 1 ? 's' : ''} eliminada${ids.length > 1 ? 's' : ''}.`,
        severity: 'success',
      });
      clearSelection();
    } catch (e) {
      setSnackbar?.({ open: true, message: e?.message || 'Error al eliminar.', severity: 'error' });
    }
  };

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, pb: 10 }}>
      {/* Buscador pill */}
      <Box
        sx={(t)=>({
          display:'flex', alignItems:'center',
          gap: 1, px: 1.25, py: 0.75, mb: 1.25,
          borderRadius: 999,
          bgcolor: alpha(t.palette.background.paper, 0.9),
          border: `1px solid ${alpha(t.palette.text.primary, 0.08)}`,
          boxShadow: `0 4px 16px ${alpha(t.palette.common.black, 0.06)}`,
        })}
      >
        <SearchIcon sx={{ opacity: 0.6 }} />
        <InputBase
          value={search}
          onChange={(e)=>setSearch(e.target.value)}
          placeholder="Buscar por nombre, eventCode o tipo…"
          inputProps={{ 'aria-label':'buscar' }}
          sx={{ flex: 1, fontSize:'0.95rem' }}
        />
      </Box>

      {/* Encabezado y acciones cuando hay selección */}
      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
          Pruebas existentes
        </Typography>

        {selectedIds.size > 0 && (
          <Button
            onClick={handleBulkDelete}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
            sx={{
              borderRadius: 2.5,
              textTransform: 'none',
              fontWeight: 800,
              px: 2,
            }}
          >
            Eliminar
          </Button>
        )}
      </Box>

      {/* Lista */}
      <List disablePadding sx={{ width: '100%' }}>
        {filtered.map((p) => (
          <PruebaItem
            key={p.id}
            p={p}
            checked={selectedIds.has(p.id)}
            onToggleCheck={toggleCheck}
            onOpenEdit={handleOpenEdit}
          />
        ))}
        {filtered.length === 0 && (
          <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
            <Typography>No se encontraron pruebas.</Typography>
          </Box>
        )}
      </List>

      {/* Modal de edición/creación */}
      <CustomPruebaModal
        isOpen={openCustomModal}
        onClose={() => setOpenCustomModal(false)}
        onSave={handleSaveCustom}
        pruebaData={editData}
        onDataChange={setEditData}
        categorias={categorias}
      />

      {/* Modal de predeterminadas */}
      <PredefinedPruebasModal
        open={openPredefModal}
        handleClose={() => setOpenPredefModal(false)}
        handleAddSelectedPruebas={onAddSelectedPruebas}
        categorias={categorias}
      />

      {/* Confirm individual (compatibilidad futura) */}
      <Dialog open={!!toDelete} onClose={() => setToDelete(null)}>
        <DialogTitle>Eliminar prueba</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Seguro que deseas eliminar “{toDelete?.nombre}”? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToDelete(null)} color="secondary">Cancelar</Button>
          <Button
            onClick={async () => {
              if (!toDelete) return;
              await onDeletePrueba?.(toDelete.id);
              setSnackbar?.({ open: true, message: 'Prueba eliminada.', severity: 'info' });
              setToDelete(null);
            }}
            color="error"
            variant="contained"
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
